import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Camera,
  Pencil,
  Trash2,
  MoreVertical,
  Fingerprint,
  Bot,
  Server,
  HardDrive,
  Calendar,
  FileText,
} from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@reflex/ui/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { PanelHeader } from '@reflex/ui/components/layout/PanelHeader';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { DetailField, DetailList } from '@reflex/ui/components/shared/DetailList';
import { CollapsibleSection } from '@/components/shared/CollapsibleSection';
import { CanGuard } from '@reflex/ui/client/permissions';
import { useDeleteSnapshot } from '@/hooks/snapshots/useSnapshots';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { formatDateTime } from '@reflex/ui/lib/format';
import type { Snapshot } from '@/lib/types';
import { RenameSnapshotDialog } from './RenameSnapshotDialog';

/** Render a byte count as a human-readable size. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

interface SnapshotDetailPanelProps {
  snapshot: Snapshot;
  onDeleted: () => void;
}

export function SnapshotDetailPanel({ snapshot, onDeleted }: SnapshotDetailPanelProps) {
  const routes = useOrgRoutes();
  const deleteSnapshot = useDeleteSnapshot();
  const [renameOpen, setRenameOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  function handleDelete() {
    deleteSnapshot.mutate(snapshot.id, {
      onSuccess: () => {
        toast.success('Snapshot deleted');
        setConfirmDeleteOpen(false);
        onDeleted();
      },
      onError: (err: Error) => {
        toast.error('Failed to delete snapshot', { description: err.message });
        setConfirmDeleteOpen(false);
      },
    });
  }

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="snapshot-detail-panel">
      <PanelHeader
        left={
          <>
            <Camera className="h-4 w-4 shrink-0 text-muted-foreground" />
            <PanelTitle as="h2" size="sm">
              {snapshot.name}
            </PanelTitle>
          </>
        }
        right={
          <CanGuard permission="blueprints:write">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="toolbar-icon"
                  className="text-muted-foreground"
                  aria-label="Snapshot actions"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setRenameOpen(true)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  disabled={deleteSnapshot.isPending}
                  onClick={() => setConfirmDeleteOpen(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </CanGuard>
        }
      />

      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          <CollapsibleSection title="Details">
            <DetailList labelWidth="w-32" striped className="w-fit max-w-full">
              <DetailField
                icon={Fingerprint}
                label="Snapshot ID"
                value={snapshot.id}
                mono
                copyable
              />
              <DetailField icon={Bot} label="Source agent">
                {snapshot.sourceAgentId ? (
                  <Link
                    to={routes.agent(snapshot.sourceAgentId)}
                    className="text-status-info hover:underline"
                  >
                    {snapshot.sourceAgentName ?? snapshot.sourceAgentId}
                  </Link>
                ) : (
                  <span className="text-sm text-foreground/90">
                    {snapshot.sourceAgentName ?? '—'}
                  </span>
                )}
              </DetailField>
              <DetailField
                icon={Server}
                label="Source devbox"
                value={snapshot.sourceDevboxId}
                mono
                copyable
              />
              {snapshot.sizeBytes != null && (
                <DetailField
                  icon={HardDrive}
                  label="Size"
                  value={formatBytes(snapshot.sizeBytes)}
                />
              )}
              <DetailField
                icon={Calendar}
                label="Created"
                value={formatDateTime(snapshot.createTimeMs)}
              />
            </DetailList>
          </CollapsibleSection>

          {snapshot.commitMessage && (
            <CollapsibleSection
              title="Description"
              icon={<FileText className="h-3.5 w-3.5 text-muted-foreground/60" />}
            >
              <p className="whitespace-pre-wrap text-sm text-foreground/90">
                {snapshot.commitMessage}
              </p>
            </CollapsibleSection>
          )}
        </div>
      </div>

      <RenameSnapshotDialog snapshot={snapshot} open={renameOpen} onOpenChange={setRenameOpen} />

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {snapshot.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This snapshot will be permanently deleted. Agents and personas pinned to it will fail
              to start until they are pointed at a different snapshot or Blueprint.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDelete}>
              Delete snapshot
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
